<template>
  <ContentHeader
    title="PI Sessions"
    :learn-more-ids="[
      'pi-start-new-session',
      'pi-duplicate-session',
      'pi-archive-session',
      'pi-csv-import',
      'pi-xlsx-download',
    ]"
  >
    <template #actions>
      <BaseButton v-if="canCreateSession" @click="create()">
        {{ isTraining ? "+ new Simulation" : "+ new PI Session" }}
      </BaseButton>
    </template>
  </ContentHeader>
  <PageBody>
    <div :class="$style['pi-plannings']">
      <div :class="$style['toolbar']">
        <div :class="$style['toolbar-left']">
          <SearchInput v-model="nameFilter" placeholder="Search" />
          <BaseCheckbox v-model="archived">Show archived</BaseCheckbox>
        </div>
      </div>

      <DataTableV2
        :data="sessions"
        :columns="tableColumns"
        :global-filter="nameFilter"
        :column-filters
      >
        <template #cell-name="{ row }">
          <span :class="$style['name-cell']">
            <SvgIcon
              v-if="row.archived"
              name="action/archive"
              width="16"
              height="16"
              :class="$style['archived-icon']"
            />
            <a
              v-if="row.editable"
              :class="[
                $style['session-link'],
                $style['name-text'],
                row.archived && $style['muted'],
              ]"
              :title="row.name"
              tabindex="0"
              role="link"
              @click="edit(row.session_id, 'session-name')"
              @keydown.enter="edit(row.session_id, 'session-name')"
            >
              {{ row.name }}
            </a>
            <span
              v-else
              :class="[$style['name-text'], row.archived && $style['muted']]"
              :title="row.name"
            >
              {{ row.name }}
            </span>
            <SessionPhaseBadge v-if="!row.archived" :session="row" />
          </span>
        </template>
        <template #cell-planning-start="{ row }">
          <span
            v-if="row.planning_dates?.start"
            :class="[$style['date-cell'], row.archived && $style['muted']]"
          >
            {{ formatDate(row.planning_dates.start) }}
          </span>
          <span v-else />
        </template>
        <template #cell-planning-end="{ row }">
          <span
            v-if="row.planning_dates?.end"
            :class="[$style['date-cell'], row.archived && $style['muted']]"
          >
            {{ formatDate(row.planning_dates.end) }}
          </span>
          <span v-else />
        </template>
        <template #cell-created="{ row }">
          <span :class="[$style['date-cell'], row.archived && $style['muted']]">{{
            formatDateTime(row.creation_date)
          }}</span>
        </template>
        <template #cell-status="{ row }">
          <AlmSyncControl
            v-if="!row.archived && row.almStatus"
            :status="row.almStatus"
            :alm-type="row.almTool"
            :editable="row.editable"
            @open="trackAlmStatusMenuOpened(row)"
            @stop="stopALMSync(row)"
            @restart="startALMSync(row)"
            @force-run="forceALMRunning(row)"
            @force-stop="stopALMSync(row)"
            @fix-config="editAlmConnection(row)"
          />
        </template>
        <template #cell-action="{ row }">
          <RowActions>
            <RowActionsButton
              v-if="row.editable"
              icon="general/edit"
              tooltip="Edit"
              aria-label="Edit"
              :data-testid="`edit-button-${row.name}`"
              @click="edit(row.session_id, 'action-menu')"
            />
            <RowActionsButton
              v-if="row.editable && !isTraining"
              icon="system/copy"
              tooltip="Duplicate"
              aria-label="Duplicate"
              @click="showDuplicateModal(row.session_id, 'action-toolbar')"
            />

            <BaseDropdown align="right" @open="trackActionMenuOpened">
              <template #trigger>
                <RowActionsMenuTrigger :data-testid="`action-menu-${row.name}`" />
              </template>
              <!-- eslint-disable vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/interactive-supports-focus -->

              <li tabindex="-1" role="menuitem" class="list-item" @click="showPiDetails(row)">
                PI Details
              </li>
              <hr :class="$style.divider" role="presentation" />

              <template v-if="row.editable">
                <li
                  tabindex="-1"
                  role="menuitem"
                  class="list-item"
                  @click="edit(row.session_id, 'action-menu')"
                >
                  Edit
                </li>
                <li
                  v-if="!isTraining"
                  tabindex="-1"
                  role="menuitem"
                  class="list-item"
                  @click="showDuplicateModal(row.session_id, 'action-menu')"
                >
                  Duplicate
                </li>
                <hr :class="$style.divider" role="presentation" />
              </template>

              <a
                tabindex="-1"
                role="menuitem"
                class="list-item"
                :href="appBoardUrl(row.session_id, 'program')"
              >
                Open PI Session in app
              </a>
              <hr :class="$style.divider" role="presentation" />

              <template v-if="row.editable">
                <li
                  tabindex="-1"
                  role="menuitem"
                  class="list-item"
                  @click="showImportModal(row.session_id, 'backlog')"
                >
                  CSV Import (Backlog Board)
                </li>
                <li
                  tabindex="-1"
                  role="menuitem"
                  class="list-item"
                  @click="showImportModal(row.session_id, 'team')"
                >
                  CSV Import (Team Boards)
                </li>
                <li
                  tabindex="-1"
                  role="menuitem"
                  class="list-item"
                  @click="showImportModal(row.session_id, 'jalign')"
                >
                  Jira Align xlsx import
                </li>
              </template>

              <li tabindex="-1" role="menuitem" class="list-item" @click="downloadXLSX(row)">
                XLSX Download
                <SvgIcon
                  v-show="xlsxDownloading"
                  name="system/loader"
                  width="14"
                  height="14"
                  :class="$style['loading']"
                />
              </li>

              <template v-if="row.editable">
                <hr :class="$style.divider" role="presentation" />

                <li
                  v-if="row.archived"
                  tabindex="-1"
                  role="menuitem"
                  class="list-item"
                  @click.prevent="unarchiveSession(row.session_id)"
                >
                  Unarchive
                </li>
                <li
                  v-else
                  tabindex="-1"
                  role="menuitem"
                  class="list-item"
                  @click="showArchiveModal(row)"
                >
                  Archive
                </li>
                <li tabindex="-1" role="menuitem" class="list-item" @click="showDeleteModal(row)">
                  Delete
                </li>
              </template>

              <!-- eslint-enable vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/interactive-supports-focus -->
            </BaseDropdown>
          </RowActions>
        </template>
      </DataTableV2>

      <DeleteModal ref="delete" title="Delete this session" @delete="deleteSession">
        Are you sure you want to delete '{{ toDelete.name }}'?
      </DeleteModal>

      <PiDetailsModal :session="detailsSession" @close="closePiDetails" />

      <ImportModal ref="import" />
      <SimulationModal ref="simulation" @changed="loadSessions" />
      <ArchiveSessionModal ref="archive" @changed="loadSessions" />
      <DuplicateModal ref="duplicate" />
    </div>
  </PageBody>
</template>

<script>
import { mapState } from "pinia";

import {
  safepiplanningsAlmStatusMenuOpened,
  safepiplanningsDuplicateSessionClicked,
  safepiplanningsEditSessionButtonClicked,
  safepiplanningsNewButtonClicked,
  safepiplanningsSessionActionMenuOpened,
  safepiplanningsSessionArchiveClicked,
  safepiplanningsSessionDeleteClicked,
  safepiplanningsSessionDetailsClicked,
  safepiplanningsUpdateALMStatusClicked,
  safepiplanningsXlsxDownloadClicked,
} from "@/analytics/events";
import { trackEvent } from "@/analytics/track";
import AlmSyncControl from "@/components-ng/AlmSyncControl.vue";
import BaseButton from "@/components-ng/BaseButton.vue";
import BaseCheckbox from "@/components-ng/BaseCheckbox.vue";
import BaseDropdown from "@/components-ng/BaseDropdown.vue";
import ContentHeader from "@/components-ng/ContentHeader.vue";
import DataTableV2 from "@/components-ng/DataTableV2.vue";
import DeleteModal from "@/components-ng/DeleteModalLegacy.vue";
import PageBody from "@/components-ng/PageBody.vue";
import RowActions from "@/components-ng/RowActions/RowActions.vue";
import RowActionsButton from "@/components-ng/RowActions/RowActionsButton.vue";
import RowActionsMenuTrigger from "@/components-ng/RowActions/RowActionsMenuTrigger.vue";
import SearchInput from "@/components-ng/SearchInput.vue";
import SessionPhaseBadge from "@/components-ng/SessionPhaseBadge.vue";
import SvgIcon from "@/components-ng/SvgIcon.vue";
import * as draftStorage from "@/components/session/sessionDraftStorage";
import { getFeatureQuery } from "@/feature";
import { AlmConfigurationHandler } from "@/handlers/almConfigurationHandler";
import SessionHandler from "@/handlers/sessionHandler";
import { SessionsHandler } from "@/handlers/sessionsHandler";
import { captureExceptionWithContext } from "@/sentry";
import { useAppStore } from "@/stores";
import { createHandlerContext } from "@/stores/context";
import { useToastStore } from "@/stores/toast";
import { downloadFile } from "@/utils";
import { relativeTimeAgo, toMediumDateString } from "@/utils/dateFormat";
import { isoDayToDate } from "@/utils/dates";
import { buildFilename } from "@/utils/filename";
import { appBoardUrl } from "@/utils/url";

import DuplicateModal from "./DuplicateModal.vue";
import ImportModal from "./ImportModal.vue";
import SimulationModal from "./SimulationModal.vue";
import ArchiveSessionModal from "./sessions/ArchiveSessionModal.vue";
import PiDetailsModal from "./sessions/PiDetailsModal.vue";

export default {
  name: "SessionsPage",
  components: {
    AlmSyncControl,
    SessionPhaseBadge,
    ContentHeader,
    PageBody,
    ImportModal,
    DataTableV2,
    DeleteModal,
    SimulationModal,
    DuplicateModal,
    BaseButton,
    BaseCheckbox,
    ArchiveSessionModal,
    PiDetailsModal,
    BaseDropdown,
    RowActions,
    RowActionsButton,
    RowActionsMenuTrigger,
    SearchInput,
    SvgIcon,
  },
  setup() {
    const toastStore = useToastStore();
    return { toastStore };
  },
  data() {
    return {
      sessions: [],
      nameFilter: "",
      xlsxDownloading: false,
      toDelete: {},
      archived: false,
      tableColumns: [
        {
          accessorKey: "name",
          header: "Name",
          enableSorting: true,
          // The only flexible column: the name gets all the room the fixed ones leave
          meta: { truncate: true, style: { width: "100%", minWidth: "200px" } },
        },
        {
          // Not rendered: it only carries the archived filter for the checkbox
          id: "archived",
          accessorFn: (row) => !!row.archived,
          header: "",
          enableSorting: false,
          enableGlobalFilter: false,
          filterFn: (row, id, showArchived) => showArchived || !row.getValue(id),
          meta: { hidden: true },
        },
        {
          id: "status",
          header: "Status",
          enableSorting: false,
          meta: { style: { minWidth: "140px" } },
        },
        {
          id: "created",
          header: "Created",
          accessorFn: (row) => row.creation_date,
          enableSorting: true,
          enableGlobalFilter: false,
          meta: { style: { width: "1px", whiteSpace: "nowrap" } },
        },
        {
          id: "action",
          header: "",
          enableSorting: false,
          meta: { style: { minWidth: "92px" } },
        },
      ],
    };
  },

  computed: {
    ...mapState(useAppStore, ["isTraining", "user"]),
    almConfigurationHandler() {
      return new AlmConfigurationHandler(createHandlerContext());
    },
    canCreateSession() {
      const sessionPerm = this.user.permissions.session;
      return sessionPerm.train.length > 0 || sessionPerm.art.length > 0;
    },
    sessionsHandler() {
      return new SessionsHandler(createHandlerContext());
    },
    columnFilters() {
      return [{ id: "archived", value: this.archived }];
    },
    // In the url so a back navigation reopens the modal. Resolves once the
    // list has loaded; until then the modal stays shut.
    detailsSession() {
      const id = this.$route.query.details;
      return this.sessions.find((session) => session.session_id === id) ?? null;
    },
  },
  async mounted() {
    // Plain instance fields: subscription handles are not view state.
    this.almStatusSubscriptions = new Map();
    this.almStatusGeneration = new Map();
    await this.loadSessions();
  },
  beforeUnmount() {
    const handles = [...this.almStatusSubscriptions.values()];
    this.almStatusSubscriptions.clear();
    void Promise.all(handles.map((handle) => handle.unsubscribe())).catch((err) =>
      captureExceptionWithContext(err),
    );
  },

  methods: {
    appBoardUrl,
    formatDate(isoDay) {
      const date = isoDayToDate(isoDay);
      return date ? toMediumDateString(date) : "";
    },
    formatDateTime(timestamp) {
      return relativeTimeAgo(timestamp);
    },
    showMessage(payload) {
      this.toastStore.showMessage(payload);
    },
    sessionHandler(sessionId) {
      return new SessionHandler(createHandlerContext(), sessionId);
    },
    async loadSessions() {
      this.sessions = await this.sessionsHandler.getSessions();
      this.sessions.sort((a, b) => (b.creation_date || 0) - (a.creation_date || 0));
      this.sessions.forEach((session) => this.getSyncStatus(session.session_id));
      // A `details` id the list does not hold - deleted, or never visible to
      // this user - would otherwise sit in the url showing nothing
      const openId = this.$route.query.details;
      if (openId && !this.sessions.some(({ session_id }) => session_id === openId)) {
        this.closePiDetails();
      }
    },
    async getSyncStatus(sessionId) {
      // Every refresh ends with at most one live listener per session, whatever
      // the session's ALM configuration says now and however refreshes overlap:
      // the previous handle is taken out before the first await, and a refresh
      // superseded while it was subscribing drops the handle it just got.
      const generation = (this.almStatusGeneration.get(sessionId) ?? 0) + 1;
      this.almStatusGeneration.set(sessionId, generation);
      const previous = this.almStatusSubscriptions.get(sessionId);
      this.almStatusSubscriptions.delete(sessionId);
      try {
        await previous?.unsubscribe();
      } catch (err) {
        void captureExceptionWithContext(err);
      }
      const sessionInfo = await this.almConfigurationHandler.getAlmConfiguration(sessionId);
      const row = this.sessions.find((session) => session.session_id === sessionId);
      if (!row) return;
      // Until this lands, "no connection" and "not asked yet" look identical
      row.almResolved = true;
      if (sessionInfo.alm_connection_id) {
        row.connectionId = sessionInfo.alm_connection_id;
        row.almTool = sessionInfo.alm_connection_type;
        row.sync_session_id = sessionInfo.sync_session_id;
        const almHandler = this.$almHandler(sessionInfo);
        const almStatus = await almHandler.getStatus();
        if (almStatus) {
          row.almStatus = almStatus;
          try {
            const handle = await almHandler.onStatus(([status]) => this.onALMStatus(row, status));
            if (this.almStatusGeneration.get(sessionId) === generation) {
              this.almStatusSubscriptions.set(sessionId, handle);
            } else {
              await handle.unsubscribe();
            }
          } catch (err) {
            void captureExceptionWithContext(err);
          }
        }
      }
    },
    edit(id, trigger) {
      trackEvent(safepiplanningsEditSessionButtonClicked(trigger));

      this.$router.push({
        path: "edit-session",
        query: {
          path: "generalInformation",
          id: id,
          ...getFeatureQuery(this.$route),
        },
      });
    },
    editAlmConnection(session) {
      trackEvent(safepiplanningsUpdateALMStatusClicked("fix", session.almTool));
      this.$router.push({
        name: "editAlmConnection",
        params: {
          connectionId: session.connectionId,
          almType: session.almTool,
        },
      });
    },
    onALMStatus(session, almStatus) {
      session.almStatus = almStatus;
    },
    async downloadXLSX(session) {
      this.xlsxDownloading = true;

      trackEvent(safepiplanningsXlsxDownloadClicked());

      try {
        const blob = await this.sessionHandler(session.session_id).getXlsx();
        downloadFile(buildFilename("session", session.name, "xlsx"), blob);
      } catch (error) {
        void captureExceptionWithContext(error);
        void this.showMessage({
          message: `Could not download XLSX: ${error.message}`,
          type: "error",
        });
      } finally {
        this.xlsxDownloading = false;
      }
    },
    async unarchiveSession(sessionId) {
      try {
        await this.sessionHandler(sessionId).unarchiveSession(sessionId);
        this.showMessage({
          message: `Unarchived successfully.`,
          type: "success",
        });
        await this.loadSessions();
      } catch (error) {
        this.showMessage({
          message: `Could not unarchive session: ${error.message}`,
          type: "error",
        });
      }
    },
    showDeleteModal(session) {
      trackEvent(safepiplanningsSessionDeleteClicked());
      this.toDelete = this.$refs.delete.show(session);
    },
    async deleteSession(sessionToDelete) {
      const toastStore = useToastStore();
      try {
        await this.sessionHandler(sessionToDelete.session_id).delete();
      } catch (error) {
        toastStore.showMessage({
          message: error?.userMessage || "Failed to delete session",
          type: "error",
        });
        this.$refs.delete.close();
        return;
      }
      this.sessions = this.sessions.filter((s) => s.session_id !== sessionToDelete.session_id);
      this.$refs.delete.close();
      toastStore.showMessage({ message: "Session deleted successfully" });
    },
    create() {
      trackEvent(safepiplanningsNewButtonClicked(this.isTraining));

      if (this.isTraining) {
        this.$refs.simulation.show();
      } else {
        this.addPiPlanning();
      }
    },
    addPiPlanning() {
      draftStorage.clearDraft();
      const appStore = useAppStore();
      appStore.clearStateManager();
      draftStorage.setSettings({ iteration_load_critical: 100 });
      this.$router.push({
        name: "addnewsafepiplanning",
        query: getFeatureQuery(this.$route),
      });
    },
    stopALMSync(pipSession) {
      this.$almHandler(pipSession).stopSession();
      trackEvent(safepiplanningsUpdateALMStatusClicked("stop", pipSession.almTool));
    },
    startALMSync(pipSession) {
      this.$almHandler(pipSession).startSession();
      trackEvent(safepiplanningsUpdateALMStatusClicked("restart", pipSession.almTool));
    },
    forceALMRunning(pipSession) {
      this.$almHandler(pipSession).forceRunningSession();
      trackEvent(safepiplanningsUpdateALMStatusClicked("force restart", pipSession.almTool));
    },
    showImportModal(session, type) {
      this.$refs.import.show(session, type);
    },
    showDuplicateModal(session, trigger) {
      trackEvent(safepiplanningsDuplicateSessionClicked(trigger));
      this.$refs.duplicate.show(session);
    },
    trackActionMenuOpened() {
      trackEvent(safepiplanningsSessionActionMenuOpened());
    },
    trackAlmStatusMenuOpened(session) {
      trackEvent(safepiplanningsAlmStatusMenuOpened(session.almTool));
    },
    showPiDetails(session) {
      trackEvent(safepiplanningsSessionDetailsClicked());
      // `push`, so closing the modal is what the back button does
      void this.$router.push({
        query: { ...this.$route.query, details: session.session_id },
      });
    },
    closePiDetails() {
      // `replace`, so back does not step into the entry that had it open. The
      // modal owns `node` and `open`; they go with it.
      const { details: _d, node: _n, open: _o, ...query } = this.$route.query;
      void this.$router.replace({ query });
    },
    showArchiveModal(session) {
      trackEvent(safepiplanningsSessionArchiveClicked());
      this.$refs.archive.show(session);
    },
  },
};
</script>

<style lang="scss" module>
@use "@/colors" as c;

.pi-plannings {
  display: flex;
  flex-direction: column;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.session-link {
  cursor: pointer;
  color: inherit;
  text-decoration: none;
  transition: color 100ms ease;

  &:hover {
    color: c.$brand-primary;
  }
}

.date-cell {
  color: c.$text-subtle;
  font-size: 13px;
}

.divider {
  width: 100%;
  margin: 0;
  border: none;
  border-bottom: 1px solid c.$border-subtle;
}

.loading {
  color: c.$icon-primary-color;
  animation-name: spin;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.name-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.name-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.archived-icon {
  color: c.$text-subtle;
  flex-shrink: 0;
}

.muted {
  color: c.$text-disabled;
}

.release-notes {
  margin-top: 1.5rem;
  padding: 1rem;
  border: 1px solid c.$border;
  border-radius: 6px;
  max-width: 400px;
  font-size: 13px;
  color: c.$text-subtle;
  line-height: 1.4;

  p {
    margin: 0 0 0.75rem;
  }
}
</style>
